/**
 * AmountVisibilityContext — global show/hide toggle for sensitive financial
 * amounts shown on dashboards & reports. The user can click an eye icon in the
 * top-right header to blur all amounts so colleagues sitting nearby can't see
 * revenue / outstanding / payroll / balances. State is persisted to localStorage
 * so it survives navigation + refresh; resets on logout if needed.
 *
 * Usage:
 *   import { Amount } from '../contexts/AmountVisibilityContext';
 *   <Amount value={123456} prefix="₹" /> // → "₹1,23,456" or "₹•••••"
 *
 *   const { hidden, toggle } = useAmountVisibility();
 *   <button onClick={toggle}>{hidden ? <EyeSlash /> : <Eye />}</button>
 */
import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';

const STORAGE_KEY = 'dx.amounts.hidden';

const AmountVisibilityContext = createContext({ hidden: false, toggle: () => {}, setHidden: () => {} });

export function AmountVisibilityProvider({ children }) {
  const [hidden, setHidden] = useState(() => {
    // Default to HIDDEN so financial amounts are private out-of-the-box.
    // The user clicks the eye icon to reveal them when needed; the choice persists
    // for the session via localStorage. This prevents colleagues from seeing
    // revenue/outstanding when the admin's screen is left open.
    try {
      const v = localStorage.getItem(STORAGE_KEY);
      return v === null ? true : v === '1';  // null → default to hidden
    } catch { return true; }
  });
  useEffect(() => {
    try { localStorage.setItem(STORAGE_KEY, hidden ? '1' : '0'); } catch { /* ignore */ }
  }, [hidden]);
  const toggle = useCallback(() => setHidden((h) => !h), []);
  const value = useMemo(() => ({ hidden, toggle, setHidden }), [hidden, toggle]);
  return <AmountVisibilityContext.Provider value={value}>{children}</AmountVisibilityContext.Provider>;
}

export function useAmountVisibility() {
  return useContext(AmountVisibilityContext);
}

/**
 * <Amount /> — renders a financial value, replaced with a blurred placeholder
 * when the global "hide amounts" toggle is on.
 *
 * Props:
 *   value       number | string  — the value to display (already formatted is fine)
 *   prefix      string           — "₹" by default; pass "" to omit
 *   suffix      string           — appended after the value (e.g. "/-")
 *   format      (n) => string    — custom formatter (default = Indian number system)
 *   mask        string           — placeholder shown when hidden (default "•••••")
 *   className   string           — passed through
 */
function defaultFormat(n) {
  const num = typeof n === 'number' ? n : Number(n);
  if (!Number.isFinite(num)) return String(n ?? '');
  return num.toLocaleString('en-IN', { maximumFractionDigits: 2 });
}

export function Amount({ value, prefix = '₹', suffix = '', format, mask = '•••••', className = '', testId }) {
  const { hidden } = useAmountVisibility();
  const formatted = (format || defaultFormat)(value);
  const display = hidden
    ? `${prefix}${mask}`
    : `${prefix}${formatted}${suffix}`;
  return (
    <span
      className={`${className} ${hidden ? 'select-none' : ''}`.trim()}
      data-testid={testId}
      data-hidden={hidden || undefined}
      title={hidden ? 'Hidden — click the eye icon in the header to show' : undefined}
    >
      {display}
    </span>
  );
}

export default AmountVisibilityContext;
